import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const Impuestos = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const impuestosData = [
    { id: 1, tipo: 'IVA', periodo: 'Enero 2026', vencimiento: '15/02/2026', monto: 850000, estado: 'Pendiente', ddjj: true },
    { id: 2, tipo: 'Ganancias', periodo: '1er Anticipo', vencimiento: '20/02/2026', monto: 2100000, estado: 'Pendiente', ddjj: false },
    { id: 3, tipo: 'IIBB Mendoza', periodo: 'Enero 2026', vencimiento: '18/02/2026', monto: 420000, estado: 'Pendiente', ddjj: true },
    { id: 4, tipo: 'Retenciones IVA', periodo: 'Enero 2026', vencimiento: '12/02/2026', monto: 185000, estado: 'Pagado', ddjj: true },
    { id: 5, tipo: 'IVA', periodo: 'Diciembre 2025', vencimiento: '15/01/2026', monto: 780000, estado: 'Pagado', ddjj: true },
  ];

  const resumenStats = [
    { label: 'A Pagar (Feb)', value: '$3.55M', color: theme.palette.warning.main },
    { label: 'Pagado (2026)', value: '$1.85M', color: theme.palette.success.main },
    { label: 'Vencidos', value: '$0', color: theme.palette.error.main },
    { label: 'DDJJ Pendientes', value: '1', color: theme.palette.info.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Impuestos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de IVA, retenciones, percepciones y DDJJ
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Cargar Pago
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color={stat.color}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabla */}
      <Card>
        <CardContent>
          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
            <Tab label="Todos" />
            <Tab label="Pendientes" />
            <Tab label="Pagados" />
            <Tab label="Vencidos" />
          </Tabs>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Impuesto</TableCell>
                  <TableCell>Período</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell align="center">DDJJ</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {impuestosData.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon color="primary" fontSize="small" />
                        <Typography variant="body2" fontWeight={500}>{row.tipo}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{row.periodo}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        {row.vencimiento}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        ${row.monto.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={row.ddjj ? 'Presentada' : 'Pendiente'}
                        size="small"
                        color={row.ddjj ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.estado}
                        size="small"
                        color={row.estado === 'Pagado' ? 'success' : row.estado === 'Vencido' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {row.estado === 'Pendiente' && (
                        <Button size="small" variant="outlined">
                          Pagar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registrar Pago de Impuesto</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Impuesto</InputLabel>
                <Select label="Impuesto">
                  <MenuItem value="iva">IVA</MenuItem>
                  <MenuItem value="ganancias">Ganancias</MenuItem>
                  <MenuItem value="iibb">IIBB</MenuItem>
                  <MenuItem value="retenciones">Retenciones</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Período" placeholder="Ej: Enero 2026" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Monto" type="number" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha de Pago" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nº Comprobante" />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                Adjuntar Comprobante (PDF)
                <input type="file" hidden />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Impuestos;
